import { Fragment, useEffect, useState } from 'react'
import classes from '../styles.module.scss'
import { useShared } from '../../../../contexts/shared.context';
import { IFarmingItemsList, IPoultryList, IScreeningFarming } from '../../../../types/screening.types';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useKato } from '../../../../contexts/kato.context';
import { updateScreening } from '../../../../requests/screening.request';
import FooterComponent from '../../components/footer.component';
import Field from '../../../../components/field/field.component';
import { isEmpty, t } from '../../../../helpers/utils';
import { toast } from 'react-toastify';

const firstGroup = ['animalHusbandry', 'meat', 'poultry', 'yearlyMeat'];
const secondGroup = ['agriculturalGrowth'];
const thirdGroup = ['selfSufficiency', 'milk'];
const fourthGroup = ['milkProduction'];
const fifthGroup = ['cropArea', 'harvest'];

const baseClass = classes['screening-tab__agriculture'];
const translationKey = 'screening.agriculture';
const keys: (keyof IScreeningFarming)[] = [
    'animalHusbandry',
    'meat',
    'poultry',
    'yearlyMeat',

    'agriculturalGrowth',

    'selfSufficiency',
    'milk',

    'milkProduction',

    'cropArea',
    'harvest',
];

const keyToTranslation: Record<keyof IScreeningFarming, string> = {
    animalHusbandry: 'animal-husbandry',
    agriculturalGrowth: 'agricultural-growth',
    selfSufficiency: 'self-sufficiency',
    poultry: 'poultry',
    milk: 'milk',
    meat: 'meat',
    cropArea: 'crop-area',
    yearlyMeat: 'yearly-meat',
    milkProduction: 'milk-production',
    harvest: 'harvest',
}

const AgricultureTab = () => {
    const { selectedInfoYear } = useShared();
    const { kato } = useParams<{ kato: string }>();
    const { i18n: { language } } = useTranslation();
    const { screeningInfo: { agriculture }, updateScreeningInfo } = useKato();
    const defaultAgriculture: IScreeningFarming = {
        animalHusbandry: {
            KPC: {
                kh: '',
                lph: '',
            },
            MPC: {
                kh: '',
                lph: '',
            },
            horses: {
                kh: '',
                lph: '',
            },
            camels: {
                kh: '',
                lph: '',
            },
        },
        agriculturalGrowth: {
            KPC: Array.from({ length: 3 }, (_, i) => ({ year: selectedInfoYear - 3 + i, value: '' })),
            MPC: Array.from({ length: 3 }, (_, i) => ({ year: selectedInfoYear - 3 + i, value: '' })),
            horses: Array.from({ length: 3 }, (_, i) => ({ year: selectedInfoYear - 3 + i, value: '' })),
            birds: Array.from({ length: 3 }, (_, i) => ({ year: selectedInfoYear - 3 + i, value: '' })),
            camels: Array.from({ length: 3 }, (_, i) => ({ year: selectedInfoYear - 3 + i, value: '' })),
        },
        selfSufficiency: {
            sheep: '',
            bird: '',
            egg: '',
            cucumber: '',
            tomato: '',
        },
        poultry: {
            goose: {
                kh: '',
                lph: '',
            },
            duck: {
                kh: '',
                lph: '',
            },
            chicken: {
                kh: '',
                lph: '',
            },
        },
        meat: {
            KPC: {
                kh: '',
                lph: '',
            },
            MPC: {
                kh: '',
                lph: '',
            },
            horses: {
                kh: '',
                lph: '',
            },
            camels: {
                kh: '',
                lph: '',
            },
        },
        milk: {
            dairy: '',
            milkProduction: '',
        },
        cropArea: {
            kh: {
                carnival: '',
                forage: '',
                grains: '',
                potato: '',
                vegetables: '',
            },
            lph: {
                carnival: '',
                forage: '',
                grains: '',
                potato: '',
                vegetables: '',
            }
        },
        yearlyMeat: {
            KPC: {
                kh: '',
                lph: '',
            },
            MPC: {
                kh: '',
                lph: '',
            },
            horses: {
                kh: '',
                lph: '',
            },
            camels: {
                kh: '',
                lph: '',
            },
        },
        milkProduction: {
            butter: {
                yearly: '',
                average: '',
            },
            cottage: {
                yearly: '',
                average: '',
            },
            sourCream: {
                yearly: '',
                average: '',
            },
            kefir: {
                yearly: '',
                average: '',
            },
            milk: {
                yearly: '',
                average: '',
            },
        },
        harvest: {
            kh: {
                carnival: '',
                forage: '',
                grains: '',
                potato: '',
                vegetables: '',
            },
            lph: {
                carnival: '',
                forage: '',
                grains: '',
                potato: '',
                vegetables: '',
            }
        },
    }

    const handleSave = () => {
        if (validateFields() && kato) {
            updateScreening(+kato, 'agriculture', formData, selectedInfoYear)
                .then(() => {
                    updateScreeningInfo();
                    toast.success(t(`admin.success`, language));
                })
                .catch((error) => {
                    console.error('Error updating agriculture:', error);
                });
        }
    };

    const validateFields = () => {
        const newErrors: Record<string, boolean> = {};

        const addError = (key: string) => {
            newErrors[key] = true;
        };

        // 1. animalHusbandry, meat, poultry, yearlyMeat
        firstGroup.forEach((key) => {
            const section = formData[key as keyof IScreeningFarming] as IFarmingItemsList | IPoultryList;
            Object.entries(section).forEach(([subKey, subValue]) => {
                const values = ['kh', 'lph'].map((fieldKey) => ({
                    fieldKey,
                    val: subValue[fieldKey],
                }));
                const allEmpty = values.every(({ val }) => isEmpty(val));
                const someFilled = values.some(({ val }) => !isEmpty(val));

                if (someFilled && !allEmpty) {
                    values.forEach(({ fieldKey, val }) => {
                        if (isEmpty(val)) {
                            addError(`${key}-${subKey}-${fieldKey}`);
                        }
                    });
                }
            });
        });

        // 2. agriculturalGrowth
        secondGroup.forEach((key) => {
            const section = formData[key as keyof IScreeningFarming] as IScreeningFarming['agriculturalGrowth'];
            Object.entries(section).forEach(([subKey, items]) => {
                const allEmpty = items.every((item) => isEmpty(item.value));
                const someFilled = items.some((item) => !isEmpty(item.value));

                if (someFilled && !allEmpty) {
                    items.forEach((item, index) => {
                        if (isEmpty(item.value)) {
                            addError(`${key}-${subKey}-${index}`);
                        }
                    });
                }
            });
        });

        // 3. selfSufficiency, milk
        thirdGroup.forEach((key) => {
            const section = formData[key as keyof IScreeningFarming] as Record<string, string>;
            const entries = Object.entries(section);
            const allEmpty = entries.every(([, val]) => isEmpty(val));
            const someFilled = entries.some(([, val]) => !isEmpty(val));

            if (someFilled && !allEmpty) {
                entries.forEach(([subKey, val]) => {
                    if (isEmpty(val)) {
                        addError(`${key}-${subKey}`);
                    }
                });
            }
        });

        // 4. milkProduction
        fourthGroup.forEach((key) => {
            const section = formData[key as keyof IScreeningFarming] as IScreeningFarming['milkProduction'];
            Object.entries(section).forEach(([subKey, subValue]) => {
                const values = ['yearly', 'average'].map((fieldKey) => ({
                    fieldKey,
                    val: subValue[fieldKey as 'yearly' | 'average'],
                }));
                const allEmpty = values.every(({ val }) => isEmpty(val));
                const someFilled = values.some(({ val }) => !isEmpty(val));

                if (someFilled && !allEmpty) {
                    values.forEach(({ fieldKey, val }) => {
                        if (isEmpty(val)) {
                            addError(`${key}-${subKey}-${fieldKey}`);
                        }
                    });
                }
            });
        });

        // 5. cropArea, harvest
        fifthGroup.forEach((key) => {
            const section = formData[key as keyof IScreeningFarming] as IScreeningFarming['cropArea'];
            Object.entries(section).forEach(([areaKey, areaData]) => {
                const entries = Object.entries(areaData);
                const allEmpty = entries.every(([, val]) => isEmpty(val));
                const someFilled = entries.some(([, val]) => !isEmpty(val));

                if (someFilled && !allEmpty) {
                    entries.forEach(([fieldKey, val]) => {
                        if (isEmpty(val)) {
                            addError(`${key}-${areaKey}-${fieldKey}`);
                        }
                    });
                }
            });
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const [formData, setFormData] = useState<IScreeningFarming>(defaultAgriculture);
    const [errors, setErrors] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (agriculture) {
            setFormData(agriculture || defaultAgriculture);
        } else {
            setFormData(defaultAgriculture);
        }
    }, [agriculture]);

    function updateNestedFormField(
        key: keyof IScreeningFarming,
        subKey: string,
        field: string,
        value: string | number
    ) {
        setFormData(prev => ({
            ...prev,
            [key]: {
                ...prev[key],
                [subKey]: {
                    ...((prev[key] as any)[subKey]),
                    [field]: value
                }
            }
        }));
    }

    function updateFlatField<T extends keyof IScreeningFarming>(
        key: T,
        subKey: string,
        value: any
    ) {
        setFormData(prev => {
            const section = prev[key] as any;

            return {
                ...prev,
                [key]: {
                    ...section,
                    [subKey]: value,
                },
            };
        });
    }

    const renderFields = (lang: 'ru' | 'kz') => {
        return (
            <div className={classes['fields-container']}>
                {keys.map((key) => {
                    const translatedKey = keyToTranslation[key];
                    const field = formData[key as keyof IScreeningFarming];
                    return <div className={classes['field-block']} key={key}>
                        <div className={classes['field-block__title']}>{t(`${translationKey}.${translatedKey}.title`, lang)}</div>
                        <div className={classes['field-block__fields']}>
                            {
                                firstGroup.includes(key) && <>
                                    {Object.entries(field).map(([subKey, subValue]) => {
                                        return (
                                            <Fragment key={`${key}-${subKey}`}>
                                                <div className={classes['field-block__title']}>{t(`${translationKey}.${translatedKey}.${subKey}`, lang)}</div>
                                                <div className={classes['field-block__fields']}>
                                                    {['kh', 'lph', 'total'].map((type) => {
                                                        const val = type === 'total' ? +subValue.kh + +subValue.lph : subValue[type] ?? '';
                                                        return (
                                                            <Field
                                                                key={`${key}-${subKey}-${type}`}
                                                                type="number"
                                                                value={val}
                                                                onChange={(newValue) => {
                                                                    const val = newValue === '' ? '' : +newValue;

                                                                    updateNestedFormField(key, subKey, type, val);
                                                                }}

                                                                label={t(
                                                                    `${translationKey}.${type === 'total' ? 'total' : 'number'}`,
                                                                    lang,
                                                                    {
                                                                        subtype: t(`${translationKey}.${translatedKey}.${subKey}`, lang),
                                                                        type: t(`${translationKey}.${type}`, lang)
                                                                    }
                                                                )}
                                                                required
                                                                disabled={type === 'total'}
                                                                error={errors[`${key}-${subKey}-${type}`]}
                                                            />
                                                        )
                                                    })}
                                                </div>
                                            </Fragment>
                                        )
                                    })}
                                </>
                            }

                            {
                                secondGroup.includes(key) && <>
                                    {Object.entries(field).map(([subKey, subValue]) => {
                                        return <Fragment key={`${key}-${subKey}`}>
                                            <div className={classes['field-block__title']}>
                                                {t(`${translationKey}.${translatedKey}.${subKey}`, lang)} ({t(`${translationKey}.${translatedKey}.number`, lang)})
                                            </div>
                                            <div className={classes['field-block__fields']}>
                                                {subValue.map((item: { year: number, value: string }, index: number) => {
                                                    const val = item.value === '' ? '' : +item.value;
                                                    return <Field
                                                        key={`${key}-${subKey}-${index}`}
                                                        type="number"
                                                        value={val}
                                                        onChange={(newValue) => {
                                                            const val = newValue === '' ? '' : +newValue;
                                                            const updated = subValue.map((i: any, idx: number) => {
                                                                if (idx === index) {
                                                                    return { ...i, value: val };
                                                                }
                                                                return i;
                                                            });

                                                            setFormData(prev => ({
                                                                ...prev,
                                                                [key]: {
                                                                    ...(prev[key] as any),
                                                                    [subKey]: updated
                                                                }
                                                            }));
                                                        }}
                                                        label={item.year.toString()}
                                                        required
                                                        error={errors[`${key}-${subKey}-${index}`]}
                                                    />
                                                })}
                                            </div>
                                        </Fragment>
                                    })
                                    }
                                </>
                            }

                            {
                                thirdGroup.includes(key) && <>
                                    {Object.keys(field).map(subKey => {
                                        const value = field[subKey as keyof typeof field] ?? '';
                                        return (
                                            <Field
                                                key={`${key}-${subKey}`}
                                                value={value}
                                                onChange={(newValue) => {
                                                    const val = newValue === '' ? '' : +newValue;
                                                    updateFlatField(key, subKey, val);
                                                }}
                                                label={t(`${translationKey}.${translatedKey}.${key === 'selfSufficiency' ? `percent-${subKey}` : subKey}`, lang)}
                                                required
                                                error={errors[`${key}-${subKey}`]}
                                            />
                                        )
                                    })}
                                </>
                            }

                            {
                                fourthGroup.includes(key) && <>
                                    {Object.entries(field).map(([subKey, subValue]) => {
                                        return (
                                            <Fragment key={`${key}-${subKey}`}>
                                                <div className={classes['field-block__title']}>{t(`${translationKey}.${translatedKey}.${subKey}`, lang)}</div>
                                                <div className={classes['field-block__fields']}>
                                                    {
                                                        ['yearly', 'average'].map((type) => {
                                                            return (
                                                                <Field
                                                                    key={`${key}-${subKey}-${type}`}
                                                                    value={subValue[type] ?? ''}
                                                                    onChange={(newValue) => {
                                                                        const val = newValue === '' ? '' : +newValue;
                                                                        updateNestedFormField(key, subKey, type, val);
                                                                    }}
                                                                    label={t(`${translationKey}.${translatedKey}.${type}`, lang)}
                                                                    required
                                                                    error={errors[`${key}-${subKey}-${type}`]}
                                                                />
                                                            );
                                                        })
                                                    }
                                                </div>
                                            </Fragment>
                                        )
                                    })}
                                </>
                            }

                            {
                                fifthGroup.includes(key) && <>
                                    {Object.entries(field).map(([subKey, subValue]) => {
                                        return (
                                            <Fragment key={`${key}-${subKey}`}>
                                                <div className={classes['field-block__title']}>{t(`${translationKey}.${subKey}`, lang)}</div>
                                                <div className={classes['field-block__fields']}>
                                                    {[...Object.entries(subValue), ['total', 0]].map(([crop, value]) => {
                                                        const total = Object.values(subValue).reduce((acc: any, curr: any) => acc + (curr ? +curr : 0), 0) as string;
                                                        const val = crop === 'total' ? total : value === '' ? '' : +((value as string) ?? 0);
                                                        return (
                                                            <Field
                                                                key={`${key}-${subKey}-${crop}`}
                                                                type="number"
                                                                value={val ?? ''}
                                                                onChange={(newValue) => {
                                                                    const val = newValue === '' ? '' : +newValue;
                                                                    updateNestedFormField(key, subKey, crop as string, val);
                                                                }}
                                                                label={t(
                                                                    `${translationKey}.${translatedKey}.number`,
                                                                    lang,
                                                                    {
                                                                        subtype: t(`${translationKey}.${translatedKey}.${crop}-number`, lang),
                                                                        type: t(`${translationKey}.${subKey}`, lang)
                                                                    }
                                                                )}
                                                                required
                                                                error={errors[`${key}-${subKey}-${crop}`]}
                                                                disabled={crop === 'total'}
                                                            />
                                                        )
                                                    })}
                                                </div>
                                            </Fragment>
                                        )
                                    })}
                                </>
                            }

                        </div>
                    </div>
                })}
            </div>
        );
    }

    return (
        <div className={classes[baseClass]}>
            <div className={classes[`grid`]}>
                {renderFields(language as 'ru' | 'kz')}
                {renderFields(language === 'ru' ? 'kz' : 'ru')}
            </div>

            <FooterComponent
                onSave={handleSave}
                validateFields={validateFields}
            />
        </div>
    );
}

export default AgricultureTab